import { useState, useCallback, useMemo, useEffect } from 'react';
import { apiUtils } from '../../../utils/apiUtils';

/**
 * Custom hook for managing scan list page with filtering, search, and operations
 * 
 * Provides scan list data specifically for the ScanList page, with tab filtering,
 * search, statistics, and scan management operations.
 * 
 * @returns {Object} Scan list data, filters, search, and actions
 */
export const useScanListPage = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedPlatform, setSelectedPlatform] = useState('');
	const [sortBy, setSortBy] = useState('startedAt');
	const [activeTab, setActiveTab] = useState('all');
	const [selectedScan, setSelectedScan] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [allScans, setAllScans] = useState([]);
	const [stats, setStats] = useState({
		totalScans: 0,
		pendingScans: 0,
		inProgressScans: 0,
		completedScans: 0,
		failedScans: 0,
	});

	const normalizeStatus = (status) => {
		if (["initializing", "queuing_jobs", "processing", "analyzing"].includes(status)) {
			return "in_progress";
		}
		// A cancelled scan belongs on the Failed tab - it produced no report.
		// Without this fold it kept its raw status and appeared on NO tab.
		if (status === "completed_with_errors" || status === "cancelled") {
			return "failed";
		}
		return status;
	};

	const fetchScans = useCallback(async ({ silent = false } = {}) => {
		try {
			if (!silent) setLoading(true);
			setError(null);
			const response = await apiUtils.get('/api/scans', {
				params: {
					search: searchTerm || undefined,
					platform: selectedPlatform || undefined,
					sortBy,
				},
			});
			const data = response.data?.data;
			const normalizedScans = (data?.scans || []).map((scan) => ({
				id: scan._id,
				_id: scan._id,
				clientName: scan.client?.name,
				clientAvatar: scan.client?.primaryAvatar,
				accountId: scan.account?._id || scan.account?.handle || scan.account?.username,
				accountHandle: scan.account?.handle || scan.account?.username,
				// hasScore/riskScore are decided by the backend (config/scanScore.js).
				// This normalizer is an allowlist: a field it does not name is
				// dropped silently, and every reader downstream sees undefined with
				// no error anywhere. The exposure chart rendered "no scored scans"
				// over 20 scored scans for exactly that reason.
				hasScore: scan.hasScore,
				riskScore: scan.riskScore,
				platform: scan.platform,
				scanType: scan.scheduledScanId ? 'Scheduled' : 'Standard',
				status: normalizeStatus(scan.status),
				startedAt: scan.startedAt || scan.createdAt,
				createdAt: scan.createdAt,
				results: scan.results,
				duration: scan.completedAt && (scan.startedAt || scan.createdAt)
					? Math.round((new Date(scan.completedAt) - new Date(scan.startedAt || scan.createdAt)) / 1000)
					: null,
			}));
			setAllScans(normalizedScans);
			// Tab counts derive from the SAME normalized list the tabs filter, so
			// a count can never disagree with what clicking the tab shows. The old
			// version counted raw statuses (initializing → "Pending") while the
			// list normalized them away (initializing → in_progress), so the
			// Pending tab could show a non-zero count over an empty list.
			const countBy = (status) => normalizedScans.filter((s) => s.status === status).length;
			setStats({
				totalScans: data?.stats?.totalScans || normalizedScans.length,
				pendingScans: countBy("pending"),
				inProgressScans: countBy("in_progress"),
				completedScans: countBy("completed"),
				failedScans: countBy("failed"),
			});
		} catch (err) {
			setError(err.message || 'Failed to load scans');
		} finally {
			setLoading(false);
		}
	}, [searchTerm, selectedPlatform, sortBy]);

	useEffect(() => {
		fetchScans();
	}, [fetchScans]);

	// Auto-refresh while any scan is active. The page otherwise fetched once on
	// mount and went stale - scans that completed in the background never updated
	// their status, post counts, or flagged totals here ("stats not updating
	// across the UI"). Poll silently (no loading flash) and stop once nothing is
	// active; the poll that observes the last scan finish also pulls its final
	// stats, so the list settles on the completed numbers.
	useEffect(() => {
		const active = (stats.inProgressScans || 0) + (stats.pendingScans || 0);
		if (active <= 0) return;
		const interval = setInterval(() => {
			if (document.hidden) return; // no polling from background tabs
			fetchScans({ silent: true });
		}, 12000);
		return () => clearInterval(interval);
	}, [stats.inProgressScans, stats.pendingScans, fetchScans]);

	// Filter and search scans
	const filteredScans = useMemo(() => {
		let filtered = [...allScans];

		// Apply tab filter
		switch (activeTab) {
			case 'pending':
				filtered = filtered.filter((scan) => scan.status === 'pending');
				break;
			case 'inProgress':
				filtered = filtered.filter((scan) => scan.status === 'in_progress');
				break;
			case 'completed':
				filtered = filtered.filter((scan) => scan.status === 'completed');
				break;
			case 'failed':
				filtered = filtered.filter((scan) => scan.status === 'failed');
				break;
			case 'all':
			default:
				break;
		}

		// Apply search filter
		if (searchTerm) {
			const term = searchTerm.toLowerCase();
			filtered = filtered.filter((scan) =>
				scan.clientName?.toLowerCase().includes(term) ||
				scan.scanType?.toLowerCase().includes(term) ||
				scan.id?.toLowerCase().includes(term)
			);
		}

		// Apply platform filter
		if (selectedPlatform) {
			filtered = filtered.filter((scan) => scan.platform === selectedPlatform);
		}

		// Apply sorting
		filtered.sort((a, b) => {
			switch (sortBy) {
				case 'startedAt':
					return new Date(b.startedAt || 0) - new Date(a.startedAt || 0);
				case 'clientName':
					return (a.clientName || '').localeCompare(b.clientName || '');
				case 'status':
					return (a.status || '').localeCompare(b.status || '');
				case 'platform':
					return (a.platform || '').localeCompare(b.platform || '');
				case 'riskScore':
					return (b.results?.riskScore || 0) - (a.results?.riskScore || 0);
				default:
					return 0;
			}
		});

		return filtered;
	}, [allScans, activeTab, searchTerm, selectedPlatform, sortBy]);

	// Scan management actions
	const viewScan = useCallback((scanId) => {
		const scan = allScans.find(s => s.id === scanId);
		setSelectedScan(scan);
		return scan;
	}, [allScans]);

	const closeScanModal = useCallback(() => {
		setSelectedScan(null);
	}, []);

	const retryScan = useCallback(async (scanId) => {
		await apiUtils.post(`/api/scans/${scanId}/retry`, {});
		await fetchScans();
	}, [fetchScans]);

	const cancelScan = useCallback(async (scanId) => {
		await apiUtils.post(`/api/scans/${scanId}/cancel`, {});
		await fetchScans();
	}, [fetchScans]);

	// Update search and filters
	const updateSearch = useCallback((term) => {
		setSearchTerm(term);
	}, []);

	const updateSorting = useCallback((newSortBy) => {
		setSortBy(newSortBy);
	}, []);

	const updateTab = useCallback((tab) => {
		setActiveTab(tab);
	}, []);

	const updatePlatformFilter = useCallback((platform) => {
		setSelectedPlatform(platform);
	}, []);

	// Reset all filters
	const resetFilters = useCallback(() => {
		setSearchTerm('');
		setSelectedPlatform('');
		setSortBy('startedAt');
		setActiveTab('all');
		setSelectedScan(null);
	}, []);

	// Refresh data
	const refresh = useCallback(async () => {
		await fetchScans();
	}, [fetchScans]);

	// Computed values. A non-"all" tab counts as a filter: a user with scans
	// who opens an empty tab used to get "No scans found - Create Your First
	// Scan", which reads as data loss. They should get "nothing matches" copy.
	const hasFilters = Boolean(searchTerm || selectedPlatform || activeTab !== 'all');
	const isEmpty = !loading && filteredScans.length === 0;
	const isFiltered = hasFilters && isEmpty;

	// Tab configuration
	const tabs = [
		{ id: 'all', label: 'All Scans', count: stats.totalScans },
		{ id: 'pending', label: 'Pending', count: stats.pendingScans },
		{ id: 'inProgress', label: 'In Progress', count: stats.inProgressScans },
		{ id: 'completed', label: 'Completed', count: stats.completedScans },
		{ id: 'failed', label: 'Failed', count: stats.failedScans },
	];

	return {
		// Data
		scans: filteredScans,
		allScans,
		selectedScan,
		loading,
		error,
		stats,
		tabs,

		// Search and filters
		searchTerm,
		selectedPlatform,
		sortBy,
		activeTab,
		hasFilters,

		// Actions
		viewScan,
		closeScanModal,
		retryScan,
		cancelScan,
		updateSearch,
		updateSorting,
		updateTab,
		updatePlatformFilter,
		resetFilters,
		refresh,

		// Computed states
		isEmpty,
		isFiltered,
	};
}; 